import React, { useCallback, useContext, useState } from 'react';

import StepModal from '@swyftx/aviary/molecules/StepModal';
import { StepModalContext, StepModalContextProvider } from '@swyftx/aviary/molecules/StepModal/StepModal.context';
import { ProtectionOrderVariant } from '@swyftx/margin/build/types';

import { observer } from 'mobx-react-lite';
import { MarginPositionDetailsStep } from 'src/lib/margin/types';

import {
  MarginPositionDetailsRootStep,
  MarginPositionDetailsClosePositionStep,
  MarginPositionDetailsProtectionOrdersStep,
  MarginPositionDetailsPartialClosePositionStep,
} from './steps';
import { MarginPositionDetailsRemoveProtectionStep } from './steps/MarginPositionDetailsRemoveProtectionStep ';

type Props = {
  initialStep?: MarginPositionDetailsStep;
  positionId?: string;
  protectionOrderType?: ProtectionOrderVariant;
  onClose: () => void;
  open: boolean;
};

const MarginPositionDetailsModalContent: React.FC<Props> = observer(
  ({ onClose, positionId, protectionOrderType, open }) => {
    const { currentStep, onNextStep, onBack, stepCount } = useContext(StepModalContext);
    const [protectionOrderToAdd, setProtectionOrderToAdd] = useState<ProtectionOrderVariant | undefined>(
      protectionOrderType,
    );
    const [protectionOrderToRemove, setProtectionOrderToRemove] = useState<ProtectionOrderVariant | undefined>(
      protectionOrderType,
    );

    const onRemoveProtectionOrder = useCallback(
      (orderType: ProtectionOrderVariant) => {
        setProtectionOrderToRemove(orderType);
        onNextStep(MarginPositionDetailsStep.RemoveProtectionOrder);
      },
      [onNextStep],
    );

    const onAddProtectionOrder = useCallback(
      (orderType: ProtectionOrderVariant) => {
        setProtectionOrderToAdd(orderType);
        onNextStep(MarginPositionDetailsStep.ManageProtectionOrders);
      },
      [onNextStep],
    );

    const onCloseProtectionOrderModal = useCallback(() => {
      if (stepCount > 1) {
        onBack();
      } else {
        onClose();
      }
    }, [onBack, onClose, stepCount]);

    return (
      <StepModal.Container
        stepIndex={currentStep}
        onClose={onClose}
        open={open}
        className='lg:min-w-[400px] lg:max-w-[400px]'
        position='dynamic'
      >
        <MarginPositionDetailsRootStep
          key={MarginPositionDetailsStep.Root}
          onClose={onClose}
          positionId={positionId}
          onNextStep={onNextStep}
          onAddProtectionOrder={onAddProtectionOrder}
          onRemoveProtectionOrder={onRemoveProtectionOrder}
        />
        <MarginPositionDetailsProtectionOrdersStep
          key={MarginPositionDetailsStep.ManageProtectionOrders}
          onClose={onCloseProtectionOrderModal}
          positionId={positionId}
          protectionOrderType={protectionOrderToAdd}
        />
        <MarginPositionDetailsClosePositionStep
          key={MarginPositionDetailsStep.ClosePosition}
          onClose={onClose}
          positionId={positionId}
        />
        <MarginPositionDetailsPartialClosePositionStep
          key={MarginPositionDetailsStep.PartialClosePosition}
          onClose={onClose}
          positionId={positionId}
        />
        <MarginPositionDetailsRemoveProtectionStep
          key={MarginPositionDetailsStep.RemoveProtectionOrder}
          onClose={onClose}
          positionId={positionId}
          protectionOrderType={protectionOrderToRemove}
        />
      </StepModal.Container>
    );
  },
);

const MarginPositionDetailsModal: React.FC<Props> = (props) => (
  <StepModalContextProvider initialStep={props.initialStep}>
    <MarginPositionDetailsModalContent {...props} />
  </StepModalContextProvider>
);

export { MarginPositionDetailsModal };
